import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/redux/root-reducers';
import { clearMessageAction, setMessageAction } from '@/redux/slicers/api-message.slice';

interface ToastProps {
  id: string;
  message: MessageOptions;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show toast
    setIsVisible(true);

    // Auto hide after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to complete
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getToastStyles = () => {
    const baseStyles =
      'fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm transform transition-all duration-300 ease-in-out';

    switch (message.type) {
      case 'success':
        return `${baseStyles} bg-green-500 text-white ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
      case 'error':
        return `${baseStyles} bg-red-500 text-white ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
      case 'warning':
        return `${baseStyles} bg-yellow-500 text-white ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
      case 'info':
        return `${baseStyles} bg-blue-500 text-white ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
      default:
        return `${baseStyles} bg-gray-500 text-white ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
    }
  };

  const getIcon = () => {
    switch (message.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '•';
    }
  };

  return (
    <div className={getToastStyles()}>
      <div className="flex items-center gap-[1rem]">
        <span className="text-[1.4] mr-2">{getIcon()}</span>
        <div className="flex-1">
          {message.messages.map((msg, index) => (
            <div key={index} className="text-[1.4rem]">
              {msg}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const dispatch = useDispatch();
  const messages = useSelector((state: RootState) => state.apiMessage.box);

  // Set dispatch for toast function
  useEffect(() => {
    setToastDispatch(dispatch);
  }, [dispatch]);

  const handleClose = (id: string) => {
    dispatch(clearMessageAction([id]));
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {Object.entries(messages).map(([id, message]) => {
        if (!message) return null;
        return <Toast key={id} id={id} message={message} onClose={() => handleClose(id)} />;
      })}
    </div>
  );
};

// Toast function để sử dụng trong hooks
let toastDispatch: any = null;

export const setToastDispatch = (dispatch: any) => {
  toastDispatch = dispatch;
};

export const toast = (options: {
  title: string;
  description: string;
  type: 'success' | 'error' | 'warning' | 'info';
}) => {
  if (!toastDispatch) {
    console.warn('Toast dispatch not set. Toast will not be displayed.');
    return;
  }

  const id = Date.now().toString();

  const message: MessageOptions = {
    type: options.type,
    messages: [options.title, options.description].filter(Boolean),
  };

  toastDispatch(setMessageAction([id, message]));

  // Auto remove after 5 seconds
  setTimeout(() => {
    toastDispatch(clearMessageAction([id]));
  }, 5000);
};
